import { useMemo, useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { startCase } from "lodash-es";

import { instance } from "@/lib/hooks";
import {
  useCMWFeatureFlagMutation,
  useCMWFeatureFlagsQuery,
} from "@/api/cmw-feature-flags";
import { FeatureFlagPayload } from "@/types";

import {
  OrganizationHierarchyResponse,
  CMWFeatureFlagWithActor,
} from "../types";

type EntityType = "organization" | "group" | "user";
type CacheUpdateMode = "toggle" | "add";

// Helper to get the correct flags array property name based on entity type
function getFlagsPropertyName(
  entityType: EntityType
): "organization_level_flags" | "group_level_flags" | "user_level_flags" {
  switch (entityType) {
    case "organization":
      return "organization_level_flags";
    case "group":
      return "group_level_flags";
    case "user":
      return "user_level_flags";
  }
}

export function useCMWFeatureFlagDetails(entityType: EntityType) {
  const queryClient = useQueryClient();
  const [updateFeatureFlag] = useCMWFeatureFlagMutation();
  const [isAddingFlag, setIsAddingFlag] = useState(false);

  const { data: hierarchyData, isLoading: hierarchyLoading } = useQuery({
    queryKey: ["cmw-feature-flags-organization-hierarchy"],
    queryFn: async () => {
      const response = await instance.get<OrganizationHierarchyResponse>(
        "/cmw-feature-flags/organization-hierarchy"
      );
      return response.data;
    },
  });

  const { data: allFlags } = useCMWFeatureFlagsQuery(undefined, {
    enabled: isAddingFlag,
  });

  const flagOptions = useMemo(() => {
    if (!allFlags) {
      return [];
    }
    return allFlags
      .map((flag) => ({
        value: flag.name,
        label: startCase(flag.name),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allFlags]);

  const handleAddFlag = useCallback(() => {
    setIsAddingFlag(true);
  }, []);

  const handleCancelAdd = useCallback(() => {
    setIsAddingFlag(false);
  }, []);

  // Unified cache update function that handles both toggle and add operations
  const updateCache = useCallback(
    (
      flagName: string,
      entityId: number,
      enabled: boolean,
      organizationId: number | undefined,
      mode: CacheUpdateMode
    ) => {
      queryClient.setQueryData<OrganizationHierarchyResponse>(
        ["cmw-feature-flags-organization-hierarchy"],
        (oldData) => {
          if (!oldData) {
            return oldData;
          }

          const orgIdToUpdate =
            entityType === "organization" ? entityId : organizationId;

          if (orgIdToUpdate === undefined) {
            return oldData;
          }

          const orgIdStr = orgIdToUpdate.toString();
          const orgData = oldData.organizations[orgIdStr];
          const allData = oldData.organizations["all"];
          const flagsProperty = getFlagsPropertyName(entityType);

          // Helper to update or add flag in an array
          const updateFlagsArray = (
            flags: CMWFeatureFlagWithActor[],
            actorId: number
          ): CMWFeatureFlagWithActor[] => {
            const existingIndex = flags.findIndex(
              (flag) => flag.name === flagName && flag.actor_id === actorId
            );

            if (existingIndex >= 0) {
              // Update existing flag
              return flags.map((flag, idx) =>
                idx === existingIndex ? { ...flag, enabled } : flag
              );
            }

            // Add new flag (only for "add" mode)
            if (mode === "add") {
              return [
                ...flags,
                {
                  name: flagName,
                  enabled,
                  actor: entityType,
                  actor_id: actorId,
                },
              ];
            }

            // For "toggle" mode, if flag doesn't exist, return unchanged
            return flags;
          };

          const newOrganizations = { ...oldData.organizations };

          // Update organization-specific data
          if (orgData) {
            const updatedOrgData = { ...orgData };
            updatedOrgData[flagsProperty] = updateFlagsArray(
              orgData[flagsProperty] || [],
              entityId
            );
            newOrganizations[orgIdStr] = updatedOrgData;
          } else if (mode === "add") {
            // Create new org data if it doesn't exist (only for add mode)
            const newOrgData = {
              organization_level_flags: [] as CMWFeatureFlagWithActor[],
              group_level_flags: [] as CMWFeatureFlagWithActor[],
              user_level_flags: [] as CMWFeatureFlagWithActor[],
            };
            newOrgData[flagsProperty] = [
              {
                name: flagName,
                enabled,
                actor: entityType,
                actor_id: entityId,
              },
            ];
            newOrganizations[orgIdStr] = newOrgData;
          }

          // Update "all" organization data (only for toggle mode)
          if (mode === "toggle" && allData) {
            const updatedAllData = { ...allData };
            updatedAllData[flagsProperty] = updateFlagsArray(
              allData[flagsProperty] || [],
              entityId
            );
            newOrganizations["all"] = updatedAllData;
          }

          return {
            ...oldData,
            organizations: newOrganizations,
          };
        }
      );
    },
    [entityType, queryClient]
  );

  const updateFlag = useCallback(
    async (
      flagName: string,
      entityId: number,
      enabled: boolean | null,
      organizationId?: number,
      cacheMode?: CacheUpdateMode
    ) => {
      const payload: FeatureFlagPayload = { enabled };
      if (entityType === "organization") {
        payload.organization_id = entityId;
      } else if (entityType === "group") {
        payload.group_id = entityId;
      } else if (entityType === "user") {
        payload.user_id = entityId;
      }

      await updateFeatureFlag({ flag: flagName, payload });

      // Update cache if we have a mode, otherwise invalidate
      if (cacheMode && enabled !== null) {
        updateCache(flagName, entityId, enabled, organizationId, cacheMode);
      } else {
        queryClient.invalidateQueries({
          queryKey: ["cmw-feature-flags-organization-hierarchy"],
        });
      }
    },
    [entityType, updateFeatureFlag, queryClient, updateCache]
  );

  const handleToggleFlag = useCallback(
    (
      flagName: string,
      enabled: boolean,
      entityId: number,
      organizationId?: number
    ) => {
      updateFlag(flagName, entityId, !enabled, organizationId, "toggle");
    },
    [updateFlag]
  );

  const handleRemoveFlag = useCallback(
    (flagName: string, entityId: number) => {
      // Invalidate query when removing a flag (no cache mode needed)
      updateFlag(flagName, entityId, null);
    },
    [updateFlag]
  );

  const handleSaveFlag = useCallback(
    async (
      values: { featureFlag: string; enabled: boolean; [key: string]: any },
      entityId: number,
      organizationId?: number
    ) => {
      await updateFlag(
        values.featureFlag,
        entityId,
        values.enabled,
        organizationId,
        "add"
      );
      setIsAddingFlag(false);
    },
    [updateFlag]
  );

  return {
    isAddingFlag,
    setIsAddingFlag,
    hierarchyData,
    hierarchyLoading,
    allFlags,
    flagOptions,
    handleAddFlag,
    handleCancelAdd,
    handleToggleFlag,
    handleRemoveFlag,
    handleSaveFlag,
  };
}
